import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { ArrowDownToLine } from 'lucide-react-native';

import { Button, Sheet, TextField } from '@/components/ui';
import { ajustarStockFormulario } from '@/services/firebase/formularios';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import type { Formulario } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  formulario: Formulario | null;
}

export function IngresarFormularioSheet({ visible, onClose, formulario }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const [cantidad, setCantidad] = useState('50');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !formulario) return;
    setCantidad('50');
    setMotivo('');
    setError(null);
    setSaving(false);
  }, [visible, formulario]);

  if (!formulario) return null;

  const cantidadN = Number(cantidad);
  const stockTras = (formulario.stock ?? 0) + (Number.isFinite(cantidadN) ? cantidadN : 0);

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (!Number.isFinite(cantidadN) || cantidadN <= 0) return setError('Cantidad inválida.');

    setSaving(true);
    try {
      await ajustarStockFormulario({
        formularioId: formulario.id,
        delta: cantidadN,
        motivo: motivo.trim() || 'Ingreso de stock',
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo ingresar stock.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title="Ingresar formularios"
      subtitle={`Sumá ejemplares al stock de ${formulario.nombre}.`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label="Ingresar stock"
            iconLeft={<ArrowDownToLine size={16} color={colors.white} />}
            onPress={onSubmit}
            loading={saving}
            disabled={!Number.isFinite(cantidadN) || cantidadN <= 0}
            variant="primary"
          />
        </>
      }
    >
      <View
        style={{
          padding: 14,
          borderRadius: radius['2xl'],
          backgroundColor: colors.crema[50],
          gap: 4,
        }}
      >
        <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 1, textTransform: 'uppercase' }}>
          Stock actual
        </Text>
        <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 28, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
          {formulario.stock}
        </Text>
      </View>

      <TextField
        label="Cantidad a ingresar"
        value={cantidad}
        onChangeText={setCantidad}
        keyboardType="numeric"
      />

      <TextField
        label="Motivo / referencia (opcional)"
        placeholder="Compra a imprenta XYZ, donación, etc."
        value={motivo}
        onChangeText={setMotivo}
        multiline
      />

      <View
        style={{
          padding: 14,
          borderRadius: radius.xl,
          backgroundColor: colors.success[50],
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text style={{ fontSize: 12, color: colors.success[600], fontWeight: typography.weights.bold, letterSpacing: 1, textTransform: 'uppercase' }}>
          Stock tras ingreso
        </Text>
        <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 22, fontWeight: typography.weights.bold, color: colors.success[600] }}>
          {Number.isFinite(stockTras) ? stockTras : '—'}
        </Text>
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
